import '../../i18n/autoTranslate';
import React, { useState, useEffect } from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const PerformanceBreakdown = ({ 
  categories = [
    { name: 'Communication', score: 85, maxScore: 100, description: 'Clear articulation and expression' },
    { name: 'Technical Skills', score: 78, maxScore: 100, description: 'Domain knowledge and expertise' },
    { name: 'Confidence', score: 92, maxScore: 100, description: 'Self-assurance and composure' },
    { name: 'Problem Solving', score: 74, maxScore: 100, description: 'Analytical thinking approach' },
    { name: 'Cultural Fit', score: 88, maxScore: 100, description: 'Alignment with company values' },
    { name: 'Leadership', score: 81, maxScore: 100, description: 'Initiative and team dynamics' }
  ],
  isLoading = false
}) => {
  const [animatedScores, setAnimatedScores] = useState(categories?.map(() => 0));
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setAnimatedScores(categories?.map(cat => cat?.score));
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [categories, isLoading]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-indigo-400';
    return 'text-yellow-400';
  };

  const getProgressBarColor = (score) => {
    if (score >= 80) return 'from-green-400 to-green-600';
    if (score >= 60) return 'from-indigo-400 to-indigo-600';
    return 'from-yellow-400 to-yellow-600';
  };

  const getScoreIcon = (score) => {
    if (score >= 80) return 'CheckCircle';
    if (score >= 60) return 'AlertCircle';
    return 'XCircle';
  };

  const radarData = categories?.map((cat, index) => ({
    category: cat?.name,
    score: animatedScores?.[index],
    fullMark: 100
  }));

  if (isLoading) {
    return (
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10 rounded-2xl p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-6 bg-gray-700/50 rounded w-48"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {[1, 2, 3]?.map(i => (
                  <div key={i} className="space-y-2">
                    <div className="h-4 bg-gray-700/50 rounded w-32"></div>
                    <div className="h-2 bg-gray-700/50 rounded"></div>
                  </div>
                ))}
              </div>
              <div className="h-64 bg-gray-700/30 rounded-lg"></div>
            </div>
          </div>
        </div>
      </SpotlightCard>
    );
  }

  return (
    <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
      <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">Performance Breakdown</h3>
            <p className="text-sm text-gray-400 mt-1">
              Detailed analysis across key interview dimensions
            </p>
          </div>
          <Icon name="BarChart3" size={24} className="text-indigo-400" />
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Category Scores */}
          <div className="space-y-4">
            {categories?.map((category, index) => (
              <div
                key={category?.name}
                className={`p-4 rounded-lg border transition-all duration-200 cursor-pointer ${
                  selectedCategory === index
                    ? 'border-indigo-500/50 bg-indigo-500/5' 
                    : 'border-gray-700 hover:border-gray-600 hover:bg-white/5'
                }`}
                onClick={() => setSelectedCategory(selectedCategory === index ? null : index)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <Icon 
                      name={getScoreIcon(animatedScores?.[index])} 
                      size={20} 
                      className={getScoreColor(animatedScores?.[index])} 
                    />
                    <div>
                      <h4 className="font-semibold text-white">{category?.name}</h4>
                      <p className="text-xs text-gray-400">{category?.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${getScoreColor(animatedScores?.[index])}`}>
                      {Math.round(animatedScores?.[index])}
                    </div>
                    <div className="text-xs text-gray-500">/ {category?.maxScore}</div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="relative">
                  <div className="w-full bg-gray-800/50 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full bg-gradient-to-r ${getProgressBarColor(animatedScores?.[index])} transition-all duration-1000 ease-out`}
                      style={{ width: `${(animatedScores?.[index] / category?.maxScore) * 100}%` }}
                    />
                  </div>
                  <div className="absolute -top-1 right-0 w-1 h-4 bg-gray-600 rounded-full opacity-50" />
                </div>

                {/* Expanded Details */}
                {selectedCategory === index && (
                  <div className="mt-4 pt-4 border-t border-gray-700 animate-fade-in">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Strengths:</span>
                        <ul className="mt-1 space-y-1 text-green-400">
                          <li className="flex items-center space-x-2">
                            <Icon name="Check" size={12} />
                            <span>Clear communication</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <Icon name="Check" size={12} />
                            <span>Good examples</span>
                          </li>
                        </ul>
                      </div>
                      <div>
                        <span className="text-gray-400">Areas to improve:</span>
                        <ul className="mt-1 space-y-1 text-yellow-400">
                          <li className="flex items-center space-x-2">
                            <Icon name="AlertTriangle" size={12} />
                            <span>More specific details</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <Icon name="AlertTriangle" size={12} />
                            <span>Reduce filler words</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Radar Chart */}
          <div className="flex flex-col">
            <div className="flex-1 min-h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="rgba(148, 163, 184, 0.2)" />
                  <PolarAngleAxis 
                    dataKey="category" 
                    tick={{ fill: 'rgba(248, 250, 252, 0.8)', fontSize: 12 }}
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 100]} 
                    tick={{ fill: 'rgba(148, 163, 184, 0.6)', fontSize: 10 }}
                  />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#6366F1"
                    fill="rgba(99, 102, 241, 0.2)"
                    strokeWidth={2}
                    dot={{ fill: '#6366F1', strokeWidth: 2, r: 4 }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Chart Legend */}
            <div className="mt-4 p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-indigo-500"></div>
                  <span className="text-gray-400">Your Performance</span>
                </div>
                <div className="text-white font-medium">
                  Average: {Math.round(animatedScores?.reduce((a, b) => a + b, 0) / animatedScores?.length)}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SpotlightCard>
  );
};

export default PerformanceBreakdown;
